import { createFileRoute } from "@tanstack/react-router";
import type { CreditEntry } from "@/lib/types";
import { Figure, Panel } from "@/ui/Panel";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, Skeleton } from "@/ui/States";
import { absoluteTime, cx, relativeTime } from "@/lib/format";
import { ENTRY_LABEL, useCredit, useRental } from "@/features/billing/rental";

export const Route = createFileRoute("/_app/o/$orgId/billing/credit")({
  component: TheBalance,
});

/**
 * The balance and every movement of it.
 *
 * # WHY EACH LINE CARRIES WHAT WAS LEFT AFTERWARDS
 *
 * Because that is how somebody checks a balance they think is wrong. A column
 * of signed amounts asks them to add fifteen numbers in their head and compare
 * the result with a figure at the top; a column of balances lets them find the
 * line where it stopped matching.
 *
 * The platform stores that figure rather than deriving it when the row is read,
 * so the number here is the number the balance actually was at that moment —
 * not a reconstruction that would quietly disagree if a row were ever inserted
 * out of order.
 */
function TheBalance() {
  const { orgId } = Route.useParams();
  const credit = useCredit(orgId);
  const rental = useRental(orgId);

  if (credit.isLoading) return <Skeleton className="h-64 w-full rounded-xl" />;
  if (credit.isError) {
    return <ErrorState error={credit.error} onRetry={() => void credit.refetch()} />;
  }
  if (!credit.data) return null;

  const { balance, entries, purchases } = credit.data;
  // The one still waiting for its payment, if there is one. Only the newest
  // matters: a second pending purchase means somebody opened two payment
  // pages, and showing both would invite them to pay twice.
  const waiting = purchases.find((p) => p.state === "pending");

  return (
    <div className="flex flex-col gap-6">
      <Panel>
        <div className="flex flex-wrap items-end gap-x-10 gap-y-4">
          <Figure
            label="Balance"
            value={balance}
            hint={
              rental.data && rental.data.monthly_micros > 0
                ? rental.data.months_affordable === 0
                  ? `not enough for another month of ${rental.data.plan}`
                  : `${rental.data.months_affordable} more ${rental.data.months_affordable === 1 ? "month" : "months"} of ${rental.data.plan}`
                : undefined
            }
          />
          {waiting && (
            <div>
              <p className="section-label">On its way</p>
              <p className="t-figure mt-1">{waiting.net}</p>
              <p className="t-meta mt-1">
                Waiting for the card processor to confirm the payment. It usually
                takes seconds.{" "}
                {waiting.checkout_url && (
                  <a
                    href={waiting.checkout_url}
                    className="text-[var(--primary)] underline decoration-[var(--border-strong)] underline-offset-2"
                  >
                    Finish paying
                  </a>
                )}
              </p>
            </div>
          )}
        </div>
      </Panel>

      <Panel
        title="Statement"
        description="Newest first. Each line says what was left afterwards, so the balance can be checked without adding up a column."
        padded={false}
      >
        {entries.length === 0 ? (
          <div className="p-4">
            <EmptyState
              title="Nothing has moved yet"
              description="Buying credit and renting capacity both appear here, with what was left after each."
            />
          </div>
        ) : (
          // Five columns, and a floor under them.
          //
          // Without one, auto layout divides a phone's 390 pixels into five
          // and every cell lands at 62 — "just now" over two lines, a money
          // figure wrapped at the decimal point, a table that fits perfectly
          // and says nothing. Below this width it scrolls inside its own
          // frame instead, which is a gesture people know.
          <Table dense minWidth="34rem">
            <thead>
              <tr>
                <Th>What</Th>
                <Th>Detail</Th>
                <Th numeric align="right">
                  Amount
                </Th>
                <Th numeric align="right">
                  Balance
                </Th>
                {/* The data face, like every other time in the product, and
                    a declared width — auto layout gave the longest column
                    the room and left this one at 62 pixels, which wraps
                    "just now" onto two lines. */}
                <Th numeric width="w-[13ch]" align="right">
                  When
                </Th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <Tr key={entry.id}>
                  <Td>
                    <span className="t-strong">{ENTRY_LABEL[entry.kind] ?? entry.kind}</span>
                  </Td>
                  <Td secondary={months(entry)}>{entry.description}</Td>
                  <Td numeric align="right">
                    <span
                      className={cx(
                        entry.micros > 0
                          ? "text-[var(--success)]"
                          : "text-[var(--text-primary)]",
                      )}
                    >
                      {entry.micros > 0 ? `+${entry.amount}` : entry.amount}
                    </span>
                  </Td>
                  <Td numeric align="right">
                    {entry.balance_after}
                  </Td>
                  <Td numeric align="right">
                    <span title={absoluteTime(entry.created_at)}>
                      {relativeTime(entry.created_at)}
                    </span>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>
    </div>
  );
}

/** The second line on a statement row: what was rented, when that is the
 *  kind of movement it was. */
function months(entry: CreditEntry): string | undefined {
  if (!entry.months || !entry.plan) return undefined;
  return `${entry.months} ${entry.months === 1 ? "month" : "months"} of ${entry.plan}`;
}
